import React, { useState, useEffect } from 'react';
import BlogPost from '../models/BlogPost';
import { ApiService } from '../services/api';
import './BlogPostDetail.css';

interface BlogPostDetailProps {
  postId: string;
  onBack: () => void;
}

interface ArchiveMonth {
  month: string;
  year: number;
  count: number;
}

const BlogPostDetail: React.FC<BlogPostDetailProps> = ({ postId, onBack }) => {
  const [post, setPost] = useState<BlogPost | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [archives, setArchives] = useState<ArchiveMonth[]>([]);

  useEffect(() => {
    fetchBlogPost();
    fetchArchives();
  }, [postId]);

  const fetchBlogPost = async () => {
    try {
      setLoading(true);
      setError(null);
      const blogPost = await ApiService.getBlogPostById(postId);
      if (blogPost) {
        setPost(blogPost);
        // Increment view count
        await ApiService.incrementBlogPostViews(postId);
      } else {
        setError('Blog post not found');
      }
    } catch (err) {
      console.error('Failed to fetch blog post:', err);
      setError('Failed to load blog post');
    } finally {
      setLoading(false);
    }
  };

  const fetchArchives = async () => {
    try {
      // Fetch all blog posts to build archives
      const allPosts = await ApiService.getBlogPosts(1, 100);
      
      // Group by month and year
      const archiveMap = new Map<string, ArchiveMonth>();
      
      allPosts.forEach(post => {
        const date = new Date(post.publishedAt);
        const month = date.toLocaleString('default', { month: 'long' });
        const year = date.getFullYear();
        const key = `${month}-${year}`;
        
        if (archiveMap.has(key)) {
          const existing = archiveMap.get(key)!;
          existing.count++;
        } else {
          archiveMap.set(key, { month, year, count: 1 });
        }
      });
      
      // Convert to array and sort by date (newest first)
      const archiveArray = Array.from(archiveMap.values()).sort((a, b) => {
        if (a.year !== b.year) return b.year - a.year;
        const monthOrder = ['January', 'February', 'March', 'April', 'May', 'June', 
                            'July', 'August', 'September', 'October', 'November', 'December'];
        return monthOrder.indexOf(b.month) - monthOrder.indexOf(a.month);
      });
      
      setArchives(archiveArray);
    } catch (err) {
      console.error('Failed to fetch archives:', err);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const handleLike = async () => {
    if (!post) return;
    try {
      await ApiService.likeBlogPost(postId);
      // Refresh the post to get updated like count
      const updatedPost = await ApiService.getBlogPostById(postId);
      if (updatedPost) {
        setPost(updatedPost);
      }
    } catch (err) {
      console.error('Failed to like post:', err);
    }
  };

  if (loading) {
    return (
      <div className="blog-post-detail-loading">
        <div className="loading-spinner"></div>
        <p>Loading blog post...</p>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div className="blog-post-detail-error">
        <h2>😕 {error || 'Blog post not found'}</h2>
        <button onClick={onBack} className="back-button">
          ← Back to Blog
        </button>
      </div>
    );
  }

  return (
    <div className="blog-post-detail-container">
      <div className="blog-post-detail-content">
        <button onClick={onBack} className="back-button">
          ← Back to Blog
        </button>

        <article className="blog-post-article">
          {post.imageUrl && (
            <div className="blog-post-hero-image">
              <img 
                src={post.imageUrl} 
                alt={post.imageAltText || post.title}
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.style.display = 'none';
                }}
              />
            </div>
          )}

          <header className="blog-post-header">
            <h1>{post.title}</h1>
            
            <div className="blog-post-meta">
              <span className="author">By {post.author}</span>
              <span className="separator">•</span>
              <span className="date">{formatDate(post.publishedAt)}</span>
              <span className="separator">•</span>
              <span className="read-time">{post.readTimeMinutes} min read</span>
              {post.category && (
                <>
                  <span className="separator">•</span>
                  <span className="category">{post.category}</span>
                </>
              )}
            </div>

            <div className="blog-post-excerpt">
              <p>{post.excerpt}</p>
            </div>

            {post.tags && post.tags.length > 0 && (
              <div className="blog-post-tags">
                {post.tags.map(tag => (
                  <span key={tag} className="tag">{tag}</span>
                ))}
              </div>
            )}
          </header>

          <div className="blog-post-body">
            {post.content && post.content.split('\n').map((paragraph, index) => (
              paragraph.trim() ? <p key={index}>{paragraph}</p> : <br key={index} />
            ))}
            {!post.content && <p>No content available</p>}
          </div>

          {post.externalLinks && post.externalLinks.length > 0 && (
            <div className="blog-post-links">
              <h3>Related Links</h3>
              <ul>
                {post.externalLinks.map((link, index) => (
                  <li key={index}>
                    <a href={link} target="_blank" rel="noopener noreferrer">
                      {link}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <footer className="blog-post-footer">
            <div className="blog-post-stats">
              <button onClick={handleLike} className="like-button">
                👍 Like ({post.likeCount})
              </button>
              <span className="views">👁 {post.viewCount} views</span>
            </div>

            {post.updatedAt && (
              <div className="blog-post-updated">
                Last updated: {formatDate(post.updatedAt)}
              </div>
            )}
          </footer>
        </article>
      </div>

      <aside className="blog-post-sidebar">
        <div className="sidebar-section author-bio">
          <h3>About the Author</h3>
          <p>
            <strong>{post.author}</strong> is a Full Stack Software Engineer with over 20 years 
            of experience helping Fortune 500 companies build scalable enterprise solutions.
          </p>
        </div>

        <div className="sidebar-section archives">
          <h3>Archives</h3>
          {archives.length > 0 ? (
            <ul className="archive-list">
              {archives.map((archive, index) => (
                <li key={index}>
                  <a href="#" onClick={(e) => {
                    e.preventDefault();
                    // TODO: Implement archive filtering
                    console.log(`Filter by ${archive.month} ${archive.year}`);
                  }}>
                    {archive.month} {archive.year} ({archive.count})
                  </a>
                </li>
              ))}
            </ul>
          ) : (
            <p className="no-archives">No archives available</p>
          )}
        </div>

        <div className="sidebar-section recent-posts">
          <h3>Recent Posts</h3>
          <p className="coming-soon">Coming soon...</p>
        </div>
      </aside>
    </div>
  );
};

export default BlogPostDetail;
